/**
 * Fields of an object shared by «Додати об'єкт» and «Налаштування»: region
 * and DSO, tariff with zone rates, current transformer at the input.
 */
import { INPUT_CT_VALUES, type InputCt, type TariffType } from 'shared';

import { useLocale } from '../i18n/locale-context.js';
import type { Locale, MessageKey } from '../i18n/messages.js';
import { fmtNumShort, parseNum } from './format.js';
import { REGIONS, osrHasRate, osrName, regionOf } from './regions.js';
import { DEFAULT_RATES, type RatedTariff, ZONES, type ZoneName, hasRates } from './tariff.js';

export function RegionFields({
  region,
  osr,
  onChange,
}: {
  region: string;
  osr: string;
  onChange: (region: string, osr: string) => void;
}) {
  const { t, locale } = useLocale();
  // A region not in the list (the former «Інша область») reads «Оберіть область».
  const known = regionOf(region);
  const list = known?.osr ?? [];
  const hint = !known
    ? null
    : !osrHasRate(osr)
      ? t('site.st.osr.none')
      : list.length > 1
        ? t('site.st.osr.many')
        : t('site.st.osr.one');
  return (
    <>
      <label className="field">
        <span>{t('site.st.region')}</span>
        <select
          value={known ? region : ''}
          onChange={(e) => onChange(e.target.value, regionOf(e.target.value)?.osr[0] ?? '')}
        >
          {!known ? <option value="">{t('site.st.region.pick')}</option> : null}
          {REGIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label[locale]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t('site.st.osr')}</span>
        <select
          value={list.length > 0 ? osr : ''}
          disabled={list.length <= 1}
          onChange={(e) => onChange(region, e.target.value)}
        >
          {list.length === 0 ? <option value="">{t('site.st.osr.missing')}</option> : null}
          {list.map((code) => (
            <option key={code} value={code}>
              {osrName(code, locale)}
            </option>
          ))}
        </select>
        {hint ? <small>{hint}</small> : null}
      </label>
    </>
  );
}

const ZONE_LABEL: Record<ZoneName, MessageKey> = {
  night: 'site.st.zone.night',
  day: 'site.st.zone.day',
  peak: 'site.st.zone.peak',
  half: 'site.st.zone.half',
  flat: 'site.st.zone.flat',
};
const TNOTE: Record<TariffType, MessageKey> = {
  dam: 'site.st.tnote.dam',
  zone2: 'site.st.tnote.zone',
  zone3: 'site.st.tnote.zone',
  fixed: 'site.st.tnote.fixed',
};

export function TariffFields({
  name,
  tariff,
  rates,
  unread,
  onChange,
}: {
  name: string;
  tariff: TariffType | '';
  rates: Partial<Record<ZoneName, string>>;
  unread: boolean;
  onChange: (tariff: TariffType, rates: Partial<Record<ZoneName, string>>) => void;
}) {
  const { t } = useLocale();
  const zone = hasRates(tariff || null) ? (tariff as RatedTariff) : null;
  return (
    <div className="group">
      <h3 className="group-title">{t('site.st.tariff')}</h3>
      <div className="pills" role="radiogroup" aria-label={t('site.st.tariff.aria')}>
        {(['dam', 'zone2', 'zone3', 'fixed'] as const satisfies readonly TariffType[]).map((v) => (
          <label key={v}>
            <input
              type="radio"
              name={name}
              value={v}
              checked={tariff === v}
              onChange={() => onChange(v, rates)}
            />
            {t(`site.st.tariff.${v}` as MessageKey)}
          </label>
        ))}
      </div>
      {zone ? (
        <div className="form-grid">
          {ZONES[zone].map((z) => (
            <label className="field" key={z}>
              <span>{t(ZONE_LABEL[z])}</span>
              <input
                inputMode="decimal"
                value={rates[z] ?? ''}
                placeholder={String(DEFAULT_RATES[zone][z] ?? '').replace('.', ',')}
                onChange={(e) => onChange(zone, { ...rates, [z]: e.target.value })}
              />
            </label>
          ))}
          {unread ? <small className="full sub">{t('site.st.zone.unread')}</small> : null}
        </div>
      ) : null}
      {tariff ? <p className="sub">{t(TNOTE[tariff])}</p> : null}
    </div>
  );
}

/** Rates typed for a zone tariff; null when one is not a number ≥ 0. */
export function parseRates(
  zone: RatedTariff,
  rates: Partial<Record<ZoneName, string>>,
): Partial<Record<ZoneName, number>> | null {
  const out: Partial<Record<ZoneName, number>> = {};
  for (const z of ZONES[zone]) {
    const v = parseNum(rates[z] ?? '');
    if (!Number.isFinite(v) || v < 0) return null;
    out[z] = v;
  }
  return out;
}

export function defaultRateTexts(zone: RatedTariff, locale: Locale): Partial<Record<ZoneName, string>> {
  return Object.fromEntries(
    Object.entries(DEFAULT_RATES[zone]).map(([k, v]) => [k, fmtNumShort(locale, v ?? 0, 2)]),
  ) as Partial<Record<ZoneName, string>>;
}

export function CtField({
  name,
  value,
  onChange,
}: { name: string; value: InputCt; onChange: (v: InputCt) => void }) {
  const { t } = useLocale();
  return (
    <div className="field full">
      <span>{t('site.st.ct')}</span>
      <div className="pills" role="radiogroup" aria-label={t('site.st.ct')}>
        {INPUT_CT_VALUES.map((v) => (
          <label key={v}>
            <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} />
            {t(`site.st.ct.${v}` as MessageKey)}
          </label>
        ))}
      </div>
      <small>{t('site.st.ct_hint')}</small>
    </div>
  );
}
